import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Gavel, Globe, ArrowRight, Loader2, CheckCircle, Smartphone, Mail, MessageCircle, ShieldCheck } from 'lucide-react';

const COUNTRY_CODES = [
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'USA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
];

const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
  { code: 'mr', name: 'Marathi', native: 'मराठी' },
  { code: 'ta', name: 'Tamil', native: 'தமிழ்' },
  { code: 'te', name: 'Telugu', native: 'తెలుగు' },
  { code: 'kn', name: 'Kannada', native: 'ಕನ್ನಡ' },
  { code: 'ml', name: 'Malayalam', native: 'മലയാളം' },
  { code: 'gu', name: 'Gujarati', native: 'ગુજરાતી' },
  { code: 'bn', name: 'Bengali', native: 'বাংলা' },
  { code: 'pa', name: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
];

const redirectForRole = (role, navigate) => {
  const normalizedRole = role?.toLowerCase();
  if (normalizedRole === 'admin') navigate('/admin');
  else if (normalizedRole === 'lawyer') navigate('/lawyer');
  else if (normalizedRole === 'agent') navigate('/agent');
  else navigate('/chat');
};

export default function LoginPage() {
  const navigate = useNavigate();
  const { registerClient, sendOTP, verifyOTP, login, startGuestChat } = useAuth();
  const [step, setStep] = useState('welcome'); // welcome | name | mobile | otp | language | success | email
  const [fullName, setFullName] = useState('');
  const [mobile, setMobile] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [otp, setOtp] = useState('');
  const [selectedLang, setSelectedLang] = useState('en');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef(null);

  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const resetFlowState = (nextStep) => {
    setError('');
    setOtp('');
    setStep(nextStep);
  };

  const startCountdown = () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setCountdown(30);
    countdownRef.current = setInterval(() => {
      setCountdown((current) => {
        if (current <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          countdownRef.current = null;
          return 0;
        }
        return current - 1;
      });
    }, 1000);
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (!fullName.trim() || fullName.trim().length < 2) {
      setError('Please enter your full name');
      return;
    }
    resetFlowState('mobile');
  };

  const handleMobileSubmit = async (e) => {
    e.preventDefault();
    const cleanMobile = mobile.replace(/\D/g, '');
    if (cleanMobile.length < 10) {
      setError('Please enter a valid mobile number');
      return;
    }
    setError('');
    setLoading(true);

    try {
      await sendOTP(cleanMobile, countryCode);
      setStep('otp');
      startCountdown();
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to send OTP. Please check the number and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    if (otp.length < 4) {
      setError('Please enter valid OTP');
      return;
    }
    setError('');
    setLoading(true);

    try {
      await verifyOTP(mobile.replace(/\D/g, ''), otp, countryCode);
      await registerClient(fullName, mobile, countryCode);
      setStep('language');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Invalid or expired OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('Please enter email and password');
      return;
    }

    setError('');
    setLoading(true);
    try {
      const data = await login(email.trim(), password);
      redirectForRole(data.user?.role, navigate);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageSelect = (lang) => {
    setSelectedLang(lang);
    localStorage.setItem('preferred_language', lang);
    setStep('success');
    setTimeout(() => navigate('/chat'), 900);
  };

  const resendOTP = async () => {
    if (countdown > 0 || loading) return;
    setError('');
    setLoading(true);
    try {
      await sendOTP(mobile.replace(/\D/g, ''), countryCode);
      startCountdown();
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#f7f7f4] text-gray-950" data-testid="login-page">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col px-5 py-5 lg:px-8">
        <header className="flex items-center justify-between border-b border-gray-200 pb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-black text-white">
              <Gavel className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight">Instadrafts</h1>
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-gray-500">AI Legal Documents</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/login/staff')}
            className="hidden rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-black hover:text-black sm:inline-flex"
          >
            Staff Portal
          </button>
        </header>

        <main className="grid flex-1 items-center gap-8 py-8 lg:grid-cols-[1.05fr_0.95fr]">
          <section className="order-2 lg:order-1">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
              <ShieldCheck className="h-4 w-4 text-green-600" /> Secure document journey
            </div>
            <h2 className="max-w-2xl font-heading text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Start instantly or log in.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-7 text-gray-600">
              Start chatting right away without any verification, use OTP or email for a personalised experience, or access the staff portal for admins, lawyers, and agents.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-4">
              <button
                type="button"
                onClick={async () => { await startGuestChat(); navigate('/chat'); }}
                className="rounded-2xl border border-black bg-black p-4 text-left text-white shadow-sm transition hover:-translate-y-0.5"
              >
                <MessageCircle className="mb-4 h-6 w-6" />
                <p className="font-semibold">Start Chat</p>
                <p className="mt-1 text-xs text-white/70">No verification needed</p>
              </button>
              <button
                type="button"
                onClick={() => resetFlowState('name')}
                className="rounded-2xl border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-black"
              >
                <Smartphone className="mb-4 h-6 w-6" />
                <p className="font-semibold">OTP Login</p>
                <p className="mt-1 text-xs text-gray-500">Mobile verification</p>
              </button>
              <button
                type="button"
                onClick={() => resetFlowState('email')}
                className="rounded-2xl border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-black"
              >
                <Mail className="mb-4 h-6 w-6" />
                <p className="font-semibold">Email Login</p>
                <p className="mt-1 text-xs text-gray-500">Password based</p>
              </button>
              <button
                type="button"
                onClick={() => navigate('/login/staff')}
                className="rounded-2xl border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-black"
              >
                <Gavel className="mb-4 h-6 w-6" />
                <p className="font-semibold">Staff Login</p>
                <p className="mt-1 text-xs text-gray-500">Admin, lawyer, agent</p>
              </button>
            </div>
          </section>

          <section className="order-1 lg:order-2">
            <div className="rounded-[2rem] border border-gray-200 bg-white p-5 shadow-xl shadow-gray-200/70 sm:p-7" data-testid="login-card">
              {step === 'welcome' && (
                <div className="space-y-5 text-center">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-black text-white">
                    <Gavel className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold">How would you like to start?</h3>
                    <p className="mt-2 text-sm text-gray-500">No account needed to chat. Login only if you want to save your documents.</p>
                  </div>
                  <div className="grid gap-3">
                    <button onClick={async () => { await startGuestChat(); navigate('/chat'); }} className="flex items-center justify-between rounded-2xl bg-black px-5 py-4 text-left font-semibold text-white transition hover:bg-gray-800">
                      Start Chat <MessageCircle className="h-5 w-5" />
                    </button>
                    <button onClick={() => resetFlowState('name')} className="flex items-center justify-between rounded-2xl border border-gray-200 px-5 py-4 text-left font-semibold transition hover:border-black">
                      Continue with OTP <ArrowRight className="h-5 w-5" />
                    </button>
                    <button onClick={() => resetFlowState('email')} className="flex items-center justify-between rounded-2xl border border-gray-200 px-5 py-4 text-left font-semibold transition hover:border-black">
                      Continue with Email <Mail className="h-5 w-5" />
                    </button>
                    <button onClick={handleGoogleLogin} className="flex items-center justify-between rounded-2xl border border-gray-200 px-5 py-4 text-left font-semibold transition hover:border-black">
                      Continue with Google <Globe className="h-5 w-5" />
                    </button>
                    <button onClick={() => navigate('/login/staff')} className="flex items-center justify-between rounded-2xl border border-gray-200 px-5 py-4 text-left font-semibold transition hover:border-black sm:hidden">
                      Staff Portal <Gavel className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}

              {step === 'email' && (
                <form onSubmit={handleEmailSubmit} className="space-y-5">
                  <div>
                    <button type="button" onClick={() => resetFlowState('welcome')} className="mb-5 text-xs font-semibold uppercase tracking-[0.18em] text-gray-400 hover:text-black">← Back</button>
                    <h3 className="font-heading text-2xl font-bold">Email Login</h3>
                    <p className="mt-2 text-sm text-gray-500">Use any active account. Staff users will be redirected to their portals.</p>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Email</label>
                    <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className="w-full rounded-2xl border border-gray-200 px-4 py-4 text-base outline-none transition focus:border-black focus:ring-2 focus:ring-black/10" placeholder="you@example.com" autoFocus required />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Password</label>
                    <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" className="w-full rounded-2xl border border-gray-200 px-4 py-4 text-base outline-none transition focus:border-black focus:ring-2 focus:ring-black/10" placeholder="••••••••" required />
                  </div>
                  {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}
                  <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-black py-4 font-semibold text-white transition hover:bg-gray-800 disabled:opacity-50">
                    {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Signing in...</> : 'Login with Email'}
                  </button>
                </form>
              )}

              {step === 'name' && (
                <form onSubmit={handleNameSubmit} className="space-y-5">
                  <button type="button" onClick={() => resetFlowState('welcome')} className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400 hover:text-black">← Back</button>
                  <div>
                    <h3 className="font-heading text-2xl font-bold">What's your name?</h3>
                    <p className="mt-2 text-sm text-gray-500">This will appear on your documents.</p>
                    <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} className="mt-5 w-full rounded-2xl border border-gray-200 px-4 py-4 text-base outline-none transition focus:border-black focus:ring-2 focus:ring-black/10" placeholder="Enter your full name" autoFocus />
                  </div>
                  {error && <p className="text-sm text-red-600">{error}</p>}
                  <button type="submit" className="flex w-full items-center justify-center gap-2 rounded-2xl bg-black py-4 font-semibold text-white transition hover:bg-gray-800">Continue <ArrowRight className="h-5 w-5" /></button>
                </form>
              )}

              {step === 'mobile' && (
                <form onSubmit={handleMobileSubmit} className="space-y-5">
                  <button type="button" onClick={() => resetFlowState('name')} className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400 hover:text-black">← Back</button>
                  <div>
                    <h3 className="font-heading text-2xl font-bold">Mobile Number</h3>
                    <p className="mt-2 text-sm text-gray-500">We'll send an OTP to verify.</p>
                    <div className="mt-5 flex gap-2">
                      <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className="rounded-2xl border border-gray-200 bg-white px-3 py-4 text-sm outline-none focus:border-black">
                        {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
                      </select>
                      <input type="tel" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} className="min-w-0 flex-1 rounded-2xl border border-gray-200 px-4 py-4 text-base outline-none transition focus:border-black focus:ring-2 focus:ring-black/10" placeholder="10-digit mobile" autoFocus />
                    </div>
                  </div>
                  {error && <p className="text-sm text-red-600">{error}</p>}
                  <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-black py-4 font-semibold text-white transition hover:bg-gray-800 disabled:opacity-50">
                    {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending OTP...</> : 'Send OTP'}
                  </button>
                </form>
              )}

              {step === 'otp' && (
                <form onSubmit={handleOTPSubmit} className="space-y-5">
                  <button type="button" onClick={() => resetFlowState('mobile')} className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400 hover:text-black">← Back</button>
                  <div className="text-center">
                    <Smartphone className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                    <h3 className="font-heading text-2xl font-bold">Enter OTP</h3>
                    <p className="mt-2 text-sm text-gray-500">Sent to {countryCode} {mobile}</p>
                  </div>
                  <div className="flex justify-center gap-2">
                    {[0, 1, 2, 3].map((i) => (
                      <input key={i} type="text" maxLength={1} value={otp[i] || ''} onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        const newOtp = otp.split('');
                        newOtp[i] = val;
                        setOtp(newOtp.join(''));
                        if (val && i < 3) document.getElementById(`otp-${i + 1}`)?.focus();
                      }} onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !otp[i] && i > 0) document.getElementById(`otp-${i - 1}`)?.focus();
                      }} id={`otp-${i}`} className="h-14 w-14 rounded-2xl border-2 border-gray-200 text-center text-2xl font-bold outline-none transition focus:border-black focus:ring-2 focus:ring-black/10" autoFocus={i === 0} />
                    ))}
                  </div>
                  {error && <p className="text-center text-sm text-red-600">{error}</p>}
                  <button type="submit" disabled={loading || otp.length < 4} className="w-full rounded-2xl bg-black py-4 font-semibold text-white transition hover:bg-gray-800 disabled:opacity-50">
                    {loading ? <><Loader2 className="mr-2 inline h-5 w-5 animate-spin" /> Verifying...</> : 'Verify OTP'}
                  </button>
                  <p className="text-center text-sm text-gray-500">Didn't receive? <button type="button" onClick={resendOTP} disabled={countdown > 0 || loading} className="font-semibold text-black disabled:text-gray-400">Resend {countdown > 0 ? `(${countdown}s)` : ''}</button></p>
                </form>
              )}

              {step === 'language' && (
                <div>
                  <h3 className="text-center font-heading text-2xl font-bold">Choose Language</h3>
                  <p className="mb-6 mt-2 text-center text-sm text-gray-500">Select your preferred language.</p>
                  <div className="grid grid-cols-2 gap-3">
                    {LANGUAGES.map(lang => (
                      <button key={lang.code} onClick={() => handleLanguageSelect(lang.code)} className={`rounded-2xl border-2 p-4 text-left transition-all ${selectedLang === lang.code ? 'border-black bg-black text-white' : 'border-gray-200 bg-white hover:border-gray-400'}`}>
                        <p className="font-bold">{lang.native}</p>
                        <p className={`text-xs ${selectedLang === lang.code ? 'text-gray-300' : 'text-gray-500'}`}>{lang.name}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 'success' && (
                <div className="py-12 text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500">
                    <CheckCircle className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold">All Set!</h3>
                  <p className="mt-2 text-sm text-gray-500">Redirecting to chat...</p>
                </div>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
